import { filter, find, toString } from "lodash";
import { useState, useMemo, useEffect } from "react";
import DataSource from "@/services/data-source";

export default function useIndexerDataSource(indexer) {
  const [allDataSources, setAllDataSources] = useState([]);
  const [dataSourcesLoaded, setDataSourcesLoaded] = useState(false);

  // Filter data sources that are suitable for indexers
  // For indexers, we want to show database types that can store indexed data
  const dataSources = useMemo(() => filter(allDataSources, ds => 
    ds.type === 'pg' || ds.type === 'psql' || ds.type === 'mysql' || 
    ds.type === 'sql' || ds.type === 'redshift' || ds.type === 'bigquery' || 
    ds.type === 'snowflake' || ds.id === indexer?.data_source_id
  ), [allDataSources, indexer?.data_source_id]);

  // Find the currently selected data source
  const dataSource = useMemo(
    () => find(dataSources, ds => toString(ds.id) === toString(indexer?.data_source_id)) || null,
    [indexer?.data_source_id, dataSources]
  );

  useEffect(() => {
    let isMounted = true;
    
    DataSource.query()
      .then(data => {
        if (isMounted) {
          // Handle both array and object response formats
          const sources = Array.isArray(data) ? data : (data.results || []);
          setAllDataSources(sources);
          setDataSourcesLoaded(true);
        }
      })
      .catch(error => {
        console.error("Error loading data sources:", error);
        if (isMounted) {
          setDataSourcesLoaded(true);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  // If we have a specific indexer ID but don't have the data source yet,
  // fetch it directly
  useEffect(() => {
    if (!indexer?.id || indexer.data_source_id || !dataSourcesLoaded) return;
    
    let isMounted = true;
    
    // For existing indexers, try to fetch the associated data source
    fetch(`/api/indexers/${indexer.id}/data_source`)
      .then(response => {
        if (!response.ok) {
          throw new Error(`Failed to fetch data source: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        if (!isMounted || !data || !data.data_source) return;
        
        // Update the allDataSources if the data source isn't already there
        setAllDataSources(prevSources => {
          const exists = find(prevSources, ds => toString(ds.id) === toString(data.data_source.id));
          if (!exists) {
            return [...prevSources, data.data_source];
          }
          return prevSources;
        });
      })
      .catch(error => {
        console.error("Error fetching indexer data source:", error);
      });
      
    return () => {
      isMounted = false;
    };
  }, [indexer?.id, indexer?.data_source_id, dataSourcesLoaded]);

  return useMemo(() => ({ 
    dataSourcesLoaded, 
    dataSources, 
    dataSource,
    allDataSources
  }), [dataSourcesLoaded, dataSources, dataSource, allDataSources]);
}